import {
  type ComponentType,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { type PillarTableFilterSelectProps } from "shared/components/pillar-table/filters/PillarTableFilterSelectMenu";

/** How one of the table's own select menus loads its options */
export type PillarTableFilterOptionSource = Pick<
  PillarTableFilterSelectProps<boolean>,
  | "choices"
  | "selectOptionQuery"
  | "queryKey"
  | "narrowByTableFilters"
  | "pagination"
  | "pageSize"
>;

type PillarTableFilterOptionSourcesContextValue = {
  /** True around the table's own filters, which render nothing and only hand over their option sources */
  isCollecting: boolean;
  /** The table's select menus' option sources, by filterKey */
  sources: Map<string, PillarTableFilterOptionSource>;
};

const PillarTableFilterOptionSourcesContext = createContext<
  PillarTableFilterOptionSourcesContextValue | undefined
>(undefined);

/** The table's option sources, or undefined unless a custom report's filters replaced the table's */
export const usePillarTableFilterOptionSources = ():
  | PillarTableFilterOptionSourcesContextValue
  | undefined => useContext(PillarTableFilterOptionSourcesContext);

/**
 * Renders the filter except while the table's own filters only hand over
 * their option sources. A hidden copy would still set its default value on
 * the table and repeat the custom report's test ids.
 */
export const withoutOptionSourceCollection = <Props extends object>(
  Filter: ComponentType<Props>,
): ComponentType<Props> => {
  const TableFilter = (props: Props) =>
    usePillarTableFilterOptionSources()?.isCollecting ? null : (
      <Filter {...props} />
    );
  return TableFilter;
};

type PillarTableFilterOptionSourcesProps = {
  /** The table's own filters, which the custom report's filters replaced */
  tableFilters: ReactNode;
  /** The custom report's filters */
  children: ReactNode;
};

/**
 * Lets a custom report's select menu that stores no choices load its options
 * the way the table's own menu of the same filterKey does, e.g. a profiles
 * default report's Category menu. The table's filters render first, in the
 * same pass, so their sources are set before the report's menus read them.
 */
const PillarTableFilterOptionSources = ({
  tableFilters,
  children,
}: PillarTableFilterOptionSourcesProps) => {
  const [sources] = useState(
    () => new Map<string, PillarTableFilterOptionSource>(),
  );
  // The table's filters mount in a node outside the document, so their
  // wrappers' labels and empty containers add nothing to the page
  const [detachedNode] = useState(() =>
    typeof document === "undefined" ? undefined : document.createElement("div"),
  );
  const collecting = useMemo(
    () => ({ isCollecting: true, sources }),
    [sources],
  );
  const reading = useMemo(() => ({ isCollecting: false, sources }), [sources]);

  return (
    <>
      <PillarTableFilterOptionSourcesContext.Provider value={collecting}>
        {detachedNode && createPortal(tableFilters, detachedNode)}
      </PillarTableFilterOptionSourcesContext.Provider>
      <PillarTableFilterOptionSourcesContext.Provider value={reading}>
        {children}
      </PillarTableFilterOptionSourcesContext.Provider>
    </>
  );
};

export default PillarTableFilterOptionSources;
